import { useState, useEffect } from 'react'
import { useWeather } from '../hooks/useWeather'
import weatherService from '../services/weatherService'
import { useTheme } from '../context/ThemeContext'
import { APP_VERSION } from '../lib/app-info'

const SettingsApp = () => {
  const { location, changeLocation, searchCities, requestLocation, clearCache, isDemo } = useWeather()
  const { wallpaperKind, wallpaperValue, setWallpaper, iconThemeId, setIconTheme, userIconColor, updateUserColor, gradientPresets, photoWallpapers, iconThemes } = useTheme()
  
  const [activeTab, setActiveTab] = useState('weather') // weather, display, system
  const [locationSearch, setLocationSearch] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [locationLoading, setLocationLoading] = useState(false)
  const [message, setMessage] = useState('')

  const [hoverPreview, setHoverPreview] = useState(null)


  // Handle location search
  const handleLocationSearch = async (query) => {
    setLocationSearch(query)
    if (query.length > 2) {
      try {
        const results = await searchCities(query)
        setSearchResults(results)
      } catch (error) {
        console.error('Search error:', error)
        setSearchResults([])
      }
    } else {
      setSearchResults([])
    }
  }

  // Handle location selection
  const handleLocationSelect = async (selectedLocation) => {
    setLocationLoading(true)
    try {
      await changeLocation(selectedLocation)
      setLocationSearch('')
      setSearchResults([])
      setMessage('Location updated successfully!')
      setTimeout(() => setMessage(''), 3000)
    } catch (error) {
      setMessage('Failed to update location')
      setTimeout(() => setMessage(''), 3000)
    } finally {
      setLocationLoading(false)
    }
  }

  // Handle geolocation request
  const handleGeolocation = async () => {
    setLocationLoading(true)
    try {
      await requestLocation()
      setMessage('Location updated using your current position!')
      setTimeout(() => setMessage(''), 3000)
    } catch (error) {
      setMessage('Failed to get your location')
      setTimeout(() => setMessage(''), 3000)
    } finally {
      setLocationLoading(false)
    }
  }


  // Handle cache clear
  const handleClearCache = () => {
    clearCache()
    setMessage('Weather cache cleared successfully!')
    setTimeout(() => setMessage(''), 3000)
  }

  const applyWallpaper = (kind, value) => {
    setWallpaper(kind, value)
  }

  const handleUpload = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      const dataUrl = ev.target.result
      applyWallpaper('custom', dataUrl)
    }
    reader.readAsDataURL(file)
  }

  const applyIconTheme = (id) => {
    setIconTheme(id)
  }

  const updateCustomIconColor = (color) => {
    updateUserColor(color)
  }

  return (
    <div className="h-full flex flex-col bg-black/20 rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div className="flex items-center space-x-4">
          <h1 className="text-xl font-bold">Settings</h1>
          <i className="fas fa-cog text-white/40"></i>
        </div>
        {message && (
          <div className="px-3 py-1 bg-green-500/20 text-green-400 text-sm rounded-full">
            <i className="fas fa-check mr-2"></i>
            {message}
          </div>
        )}
      </div>

      {/* Navigation Tabs */}
      <div className="flex border-b border-white/10">
        <button
          onClick={() => setActiveTab('weather')}
          className={`flex-1 p-3 text-sm font-medium transition-colors ${
            activeTab === 'weather'
              ? 'bg-blue-500/20 text-blue-400 border-b-2 border-blue-400'
              : 'text-white/70 hover:text-white hover:bg-white/5'
          }`}
        >
          <i className="fas fa-cloud-sun mr-2"></i>
          Weather
        </button>
        <button
          onClick={() => setActiveTab('display')}
          className={`flex-1 p-3 text-sm font-medium transition-colors ${
            activeTab === 'display'
              ? 'bg-blue-500/20 text-blue-400 border-b-2 border-blue-400'
              : 'text-white/70 hover:text-white hover:bg-white/5'
          }`}
        >
          <i className="fas fa-desktop mr-2"></i>
          Display
        </button>
        <button
          onClick={() => setActiveTab('system')}
          className={`flex-1 p-3 text-sm font-medium transition-colors ${
            activeTab === 'system'
              ? 'bg-blue-500/20 text-blue-400 border-b-2 border-blue-400'
              : 'text-white/70 hover:text-white hover:bg-white/5'
          }`}
        >
          <i className="fas fa-microchip mr-2"></i>
          System
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'weather' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Weather Settings</h3>
              
              {/* Current Location */}
              <div className="bg-white/5 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium">Current Location</h4>
                  {isDemo && (
                    <span className="px-2 py-1 bg-pink-500/20 text-pink-400 text-xs rounded-full">
                      Demo Mode
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2 mb-4">
                  <i className="fas fa-map-marker-alt text-blue-400"></i>
                  <span className="text-white/80">{location?.name || 'No location set'}</span>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={handleGeolocation}
                    disabled={locationLoading}
                    className="px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg transition-colors text-sm disabled:opacity-50"
                  >
                    {locationLoading ? (
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                    ) : (
                      <i className="fas fa-crosshairs mr-2"></i>
                    )}
                    Use My Location
                  </button>
                </div>
              </div>

              {/* Location Search */}
              <div className="bg-white/5 rounded-lg p-4 mb-4">
                <h4 className="font-medium mb-3">Change Location</h4>
                <div className="relative mb-3">
                  <input
                    type="text"
                    placeholder="Search for a city..."
                    value={locationSearch}
                    onChange={(e) => handleLocationSearch(e.target.value)}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-blue-400"
                  />
                  {searchResults.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-black/80 border border-white/20 rounded-lg max-h-32 overflow-y-auto z-10">
                      {searchResults.map((city, index) => (
                        <button
                          key={index}
                          onClick={() => handleLocationSelect(city)}
                          className="w-full p-3 text-left hover:bg-white/10 transition-colors text-sm border-b border-white/10 last:border-b-0"
                        >
                          {city.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>


              {/* Cache Management */}
              <div className="bg-white/5 rounded-lg p-4">
                <h4 className="font-medium mb-3">Cache Management</h4>
                <p className="text-sm text-white/60 mb-3">
                  Clear cached weather data to force refresh from the API.
                </p>
                <button
                  onClick={handleClearCache}
                  className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-colors text-sm"
                >
                  <i className="fas fa-trash mr-2"></i>
                  Clear Weather Cache
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'display' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Display Settings</h3>
              <div className="bg-white/5 rounded-lg p-4 mb-4 space-y-6">
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2"><i className="fas fa-image"></i> Wallpaper</h4>
                  <div className="flex flex-wrap gap-2 mb-3">
                    <button onClick={() => applyWallpaper('gradient', wallpaperValue)} className={`px-3 py-1 rounded-full text-xs ${wallpaperKind==='gradient' ? 'bg-pink-500/30 text-pink-300' : 'bg-white/10 hover:bg-white/15'}`}>Gradients</button>
                    <button onClick={() => applyWallpaper('photo', photoWallpapers[0]?.id || '')} className={`px-3 py-1 rounded-full text-xs ${wallpaperKind==='photo' ? 'bg-pink-500/30 text-pink-300' : 'bg-white/10 hover:bg-white/15'}`}>Photos</button>
                    <label className="px-3 py-1 rounded-full text-xs bg-white/10 hover:bg-white/15 cursor-pointer">Upload<input type="file" accept="image/*" className="hidden" onChange={handleUpload} /></label>
                  </div>
                  {wallpaperKind === 'gradient' && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {gradientPresets.map(g => (
                        <button key={g.id} onMouseEnter={()=>setHoverPreview({kind:'gradient',value:g.id})} onMouseLeave={()=>setHoverPreview(null)} onClick={() => applyWallpaper('gradient', g.id)} className={`h-16 rounded-lg relative overflow-hidden border border-white/10 ${wallpaperValue===g.id && wallpaperKind==='gradient' ? 'ring-2 ring-pink-400' : ''}`}> <div className={`absolute inset-0 ${g.classes}`}></div><span className="absolute bottom-1 left-1 text-[10px] bg-black/40 px-1 rounded">{g.name}</span></button>
                      ))}
                    </div>
                  )}
                  {wallpaperKind === 'photo' && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {photoWallpapers.map(p => (
                        <button key={p.id} onMouseEnter={()=>setHoverPreview({kind:'photo',value:p.id})} onMouseLeave={()=>setHoverPreview(null)} onClick={() => applyWallpaper('photo', p.id)} className={`h-16 rounded-lg relative overflow-hidden border border-white/10 ${wallpaperKind==='photo' && wallpaperValue===p.id ? 'ring-2 ring-pink-400' : ''}`}> <div className="absolute inset-0 bg-cover bg-center" style={{backgroundImage:`url('${p.src}')`}}></div><span className="absolute bottom-1 left-1 text-[10px] bg-black/40 px-1 rounded">{p.name}</span></button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2"><i className="fas fa-palette"></i> Icon Theme</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {iconThemes.map(theme => (
                      <button key={theme.id} onClick={() => applyIconTheme(theme.id)} className={`p-3 rounded-lg border border-white/10 text-left text-xs hover:bg-white/10 transition ${iconThemeId===theme.id ? 'ring-2 ring-pink-400 theme-fade' : ''}`}>
                      <div className="flex items-center gap-2 mb-2">
                      <div className="flex -space-x-1">
                        {theme.colors.map((c,i)=>(
                          <span key={i} className="w-3 h-3 rounded-full border border-white/20" style={{background:c}} />
                        ))}
                        {theme.colors.length > 4 && (
                          <span className="w-3 h-3 rounded-full border border-white/20 bg-black/50 text-[8px] flex items-center justify-center">
                            +{theme.colors.length - 4}
                          </span>
                        )}
                      </div>
                          <span className="font-medium">{theme.name}</span>
                      </div>
                      <div className="flex items-center gap-1 text-[9px] text-white/50"><i className="fas fa-icons"></i> {theme.strategy}</div>
                      </button>
                    ))}
                  </div>
                  {iconThemeId === 'userColor' && (
                    <div className="mt-3 flex items-center gap-3">
                      <input type="color" value={userIconColor} onChange={e=>updateCustomIconColor(e.target.value)} className="w-10 h-10 rounded" />
                      <span className="text-xs text-white/60">Pick your icon color</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'system' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">System Information</h3>
              
              <div className="bg-white/5 rounded-lg p-4 mb-4">
                <h4 className="font-medium mb-3">Pink Pixel OS</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/70">Version</span>
                    <span>{APP_VERSION}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/70">Build</span>
                    <span>React 18.3.1</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/70">Framework</span>
                    <span>Vite 4.5.14</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/70">Styling</span>
                    <span>Tailwind CSS 3.4.17</span>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 rounded-lg p-4 mb-4">
                <h4 className="font-medium mb-3">Performance</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/70">Memory Usage</span>
                    <span className="text-green-400">Optimized</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/70">Render Performance</span>
                    <span className="text-green-400">60 FPS</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/70">Bundle Size</span>
                    <span className="text-blue-400">Compressed</span>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 rounded-lg p-4">
                <h4 className="font-medium mb-3">About</h4>
                <p className="text-sm text-white/60 mb-3">
                  Pink Pixel OS is a modern desktop simulator built with React. 
                  Experience the future of desktop computing in your browser.
                </p>
                <div className="flex items-center space-x-4 text-sm">
                  <a 
                    href="https://pinkpixel.dev" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-pink-400 hover:text-pink-300 underline"
                  >
                    <i className="fas fa-external-link-alt mr-1"></i>
                    pinkpixel.dev
                  </a>
                  <a 
                    href="https://github.com/pinkpixel-dev" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 underline"
                  >
                    <i className="fab fa-github mr-1"></i>
                    GitHub
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SettingsApp
